import { zodResolver } from "@hookform/resolvers/zod";
import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CreateErfinaProductRequestDto } from "../../dtos/erfina-product-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { usePostCreateErfinaProductMutation } from "../mutations/use-post-create-erfina-product-mutation";
import { usePutUpdateErfinaProductMutation } from "../mutations/use-put-update-erfina-product-mutation";
import { useGetErfinaProductDetailQuery } from "../queries/use-get-erfina-product-detail-query";
import {
  type ErfinaProductFormSchema,
  useErfinaProductFormSchema
} from "../schemas/use-erfina-product-form-schema";

type UseErfinaProductFormProps = {
  productId: string;
  onSuccess?: () => void;
};

type UseErfinaProductFormReturn = {
  formMethods: UseFormReturn<ErfinaProductFormSchema>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
  isLoadingDetail: boolean;
};

export const useErfinaProductForm = ({
  productId,
  onSuccess
}: UseErfinaProductFormProps): UseErfinaProductFormReturn => {
  const { t } = useTranslation("product");
  const queryClient = useQueryClient();
  const schema = useErfinaProductFormSchema();

  const formMethods = useForm<ErfinaProductFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      lampu_product_id: "",
      product_family: "",
      product_code_debit: "",
      coa_debit: "",
      product_code_credit: "",
      coa_credit: ""
    }
  });

  const {
    data: detailData,
    isLoading: isLoadingDetail,
    error: detailError
  } = useGetErfinaProductDetailQuery({
    productId,
    options: {
      enabled: !!productId,
      retry: false
    }
  });

  const isExisting = !!detailData?.data && !detailError;

  // Populate form with existing data
  useEffect(() => {
    if (detailData?.data && !detailError) {
      const {
        product_code_debit,
        product_family,
        coa_debit,
        product_code_credit,
        coa_credit,
        lampu_product_id
      } = detailData.data;

      formMethods.reset({
        lampu_product_id: lampu_product_id || productId,
        product_family: product_family || "",
        product_code_debit,
        coa_debit,
        product_code_credit,
        coa_credit
      });
    }
  }, [detailData, detailError, formMethods, productId]);

  // Clear form on 404
  useEffect(() => {
    if (detailError && detailError instanceof HTTPError) {
      if (detailError.response.status === 404) {
        formMethods.reset({
          lampu_product_id: productId,
          product_family: "",
          product_code_debit: "",
          coa_debit: "",
          product_code_credit: "",
          coa_credit: ""
        });
      }
    }
  }, [detailError, formMethods, productId]);

  const createMutation = usePostCreateErfinaProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.erfinaModal.notifications.successCreate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.erfinaModal.notifications.failedToCreate"
            )
          });
        }
      }
    }
  });

  const updateMutation = usePutUpdateErfinaProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.erfinaModal.notifications.successUpdate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.erfinaModal.notifications.failedToUpdate"
            )
          });
        }
      }
    }
  });

  const onSubmit = formMethods.handleSubmit(async (data) => {
    const payload: CreateErfinaProductRequestDto = {
      lampu_product_id: data.lampu_product_id || productId,
      product_family: data.product_family ?? "",
      product_code_debit: data.product_code_debit ?? "",
      coa_debit: data.coa_debit ?? "",
      product_code_credit: data.product_code_credit ?? "",
      coa_credit: data.coa_credit ?? ""
    };

    if (isExisting) {
      await updateMutation.mutateAsync({
        ...payload,
        productId
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
  });

  return {
    formMethods,
    onSubmit,
    isPending: createMutation.isPending || updateMutation.isPending,
    isLoadingDetail
  };
};
